import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectAllHubs } from '../modules/hubs/stores/slice';
import { doCreateHub, doGetHub, doGetHubs, doRemoveHub, doUpdateHub } from '../modules/hubs/stores/thunk';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PAGE_SIZE } from '../constants';
import { Button, Input, Menu, Modal, Popconfirm, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { ReactSVG } from 'react-svg';
import queryString from 'query-string';
import HubForm from '../components/Forms/HubForm';
import { getStoredPageSize } from '../hooks/useColumnConfig';
import HubsTable from '../components/Tables/HubsTable';
import './Hubs.scss';
import FleetsTable from '../components/Tables/FleetsTable';
import GoogleMap from '../components/Map/GoogleMap';
import { setShouldFitbounds } from '../redux/slice';

const initStateModal = {
  open: false,
  hub: null,
  action: 'ide',
};

const HubsPage = () => {
  const { t } = useTranslation('hubs');
  const { t: tc } = useTranslation('common');

  const itemsMenu = [
    {
      label: <div className="MenuItem">{t('listView')}</div>,
      key: 'list_view',
    },
    {
      label: <div className="MenuItem">{t('mapView')}</div>,
      key: 'map_view',
    },
  ];

  const fleetsColumns = [
    {
      title: tc('name'),
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 200,
    },
    {
      title: tc('tag'),
      dataIndex: 'tag',
      key: 'tag',
      width: 70,
      render: (value) => {
        return <span className={'status ' + value.toLowerCase()}>{tc(`fleetTags.${value}`)}</span>;
      },
    },
    {
      title: tc('status'),
      dataIndex: 'status',
      key: 'status',
      fixed: 'right',
      width: 200,
      render: (value) => {
        return <span className={'status fleet ' + value.toLowerCase()}>{tc(`fleetStatuses.${value}`)}</span>;
      },
    },
  ];
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isFetching, action, status, metadata, currentHub } = useSelector((state) => state.hub);
  const hubs = useSelector((state) => selectAllHubs(state));

  const [viewMode, setViewMode] = useState('list_view');
  const [searchValue, setSearchValue] = useState('');
  const [searchParams] = useSearchParams();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [stateModal, setStateModal] = useState(initStateModal);
  const [selectedHubId, setSelectedHubId] = useState();
  const tableConfigRef = useRef({});

  const query = Object.fromEntries(searchParams);
  const currentPage = query.current || 1;
  // Page size lives under the gear (house rule) — the drawer persists it per
  // table; the URL still wins so shared links keep their pagination.
  const itemsPerPage = query.pageSize || getStoredPageSize('hubs-columns', PAGE_SIZE);
  const pagination = {
    current: currentPage || 1,
    pageSize: Number(itemsPerPage),
    total: metadata.totalItems || hubs.length,
    showSizeChanger: false,
    showTotal: (total) => tc('totalItems', { total, entity: t('title') }),
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const onSelectHub = (id) => {
    if (selectedHubId === id) {
      handleCancelSelectedHub();
    } else {
      setSelectedHubId(id);
    }
  };

  const handleCancelSelectedHub = () => {
    setSelectedHubId(undefined);
  };

  useEffect(() => {
    if (['create', 'update', 'remove']?.includes(action) && status === 'succeeded') {
      if (viewMode === 'list_view') {
        dispatch(doGetHubs({ page: currentPage, take: itemsPerPage, ...query }));
      } else {
        dispatch(doGetHubs({ page: 1, take: 100000 }));
      }
      handleCancelModal();
    }
  }, [isFetching]);

  useEffect(() => {
    if (hubs.length > 0) {
      setMarkers(
        hubs.map((hub) => {
          return {
            id: hub.id,
            content:
              selectedHubId === hub.id ? (
                <img src="/icons/hub-detail-active.svg" />
              ) : (
                <img src="/icons/hub-detail.svg" />
              ),
            latitude: Number(hub.startAddressLat),
            longitude: Number(hub.startAddressLng),
            offsetX: 0,
            offsetY: 0,
            className: 'hub-marker',
            onClick: () => onSelectHub(hub.id),
          };
        }),
      );
    } else {
      setMarkers([]);
    }
  }, [hubs, selectedHubId]);

  useEffect(() => {
    if (selectedHubId) {
      dispatch(doGetHub({ id: selectedHubId }));
    }
  }, [selectedHubId]);

  useEffect(() => {
    setSearchValue(query.q || '');
  }, []);

  useEffect(() => {
    if (viewMode === 'list_view') {
      dispatch(doGetHubs({ page: currentPage, take: itemsPerPage, ...query }));
    } else {
      dispatch(doGetHubs({ page: 1, take: 100000 }));
      dispatch(setShouldFitbounds(true));
    }
  }, [viewMode]);

  const handleCancelModal = () => {
    setStateModal(initStateModal);
  };

  const pushTo = (pushProps) => {
    navigate({
      pathname: pushProps.path || location.pathname,
      search: queryString.stringify(pushProps.query || {}, {
        skipNull: true,
      }),
      state: pushProps.state,
    });
  };

  const handleSubmitModal = (data) => {
    if (stateModal.action === 'new') {
      dispatch(doCreateHub(data));
    } else {
      data.id = stateModal.hub.id;
      dispatch(doUpdateHub(data));
    }
  };

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    if (updatePath) {
      pushTo({ query: params });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetHubs({
        page: params.current || newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  const onClickNew = () => {
    setStateModal({
      ...stateModal,
      open: true,
      hub: null,
      action: 'new',
    });
  };

  const onClickEdit = (record) => {
    setStateModal({
      ...stateModal,
      open: true,
      hub: record,
      action: 'edit',
    });
  };

  const onClickDelete = (record) => {
    dispatch(doRemoveHub({ id: record.id }));
  };

  const handleTableChange = (params) => {
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  const withAction = (record) => {
    return (
      <div className="actions">
        <Button type="link" className="edit" onClick={() => onClickEdit(record)}>
          <ReactSVG className="icon" src="/icons/edit.svg" />
        </Button>
        <Popconfirm
          title={tc('areYouSureDelete')}
          onConfirm={() => onClickDelete(record)}
          okText={tc('yes')}
          cancelText={tc('no')}
        >
          <Button type="link" className="more">
            <ReactSVG className="icon" src="/icons/delete_forever.svg" />
          </Button>
        </Popconfirm>
      </div>
    );
  };

  return (
    <Spin spinning={isFetching}>
      <div className="HubsPage Content">
        <div className="ContentHeader">
          <div className="HeaderTitle">{t('title')}</div>
          <div className="HeaderToolBar">
            <div className="ToolBarButtons">
              <Button className="CreateButton ButtonPrimary" type="primary" size="small" onClick={onClickNew}>
                {t('createNewHub')}
                <ReactSVG className="icon" src="/icons/plus.svg" />
              </Button>
            </div>
          </div>
        </div>
        <div className="ContentSelect">
          <Menu selectedKeys={[viewMode]} mode="horizontal" onClick={({ key }) => setViewMode(key)} items={itemsMenu} />
        </div>
        {viewMode === 'list_view' && (
          <>
            <div className="page-header-table">
              <div className="search">
                <Input
                  onChange={handleChangeSearch}
                  onBlur={handleSearch}
                  placeholder={tc('search')}
                  onKeyDown={handleKeyDown}
                  suffix={<img src="/icons/search.svg" />}
                  disabled={isFetching}
                  value={searchValue}
                />
              </div>
              <div className="buttons">
                <Button className="FilterButton ButtonFilter">
                  {selectedRowKeys?.length > 0 && <div className="count">{selectedRowKeys.length}</div>}
                  {tc('action')} <ReactSVG className="icon" src="/icons/arrow_drop_down.svg" />
                </Button>
                <Button
                  type="text"
                  onClick={() => tableConfigRef.current?.openDrawer?.()}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <ReactSVG className="icon" src="/icons/settings.svg" />
                </Button>
              </div>
            </div>

            <div className="ContentMain">
              <HubsTable
                dataSource={hubs}
                pagination={pagination}
                withAction={withAction}
                rowKey="id"
                isFetching={false}
                onSelectChange={onSelectChange}
                onChange={handleTableChange}
                configRef={tableConfigRef}
                onPageSizeChange={(size) => handleTableChange({ pageSize: size, current: 1 })}
              />
            </div>
          </>
        )}

        {viewMode === 'map_view' && (
          <div className="map-view">
            <div className="wrapper-map">
              <GoogleMap
                markers={markers}
                center={
                  currentHub && selectedHubId
                    ? {
                        lat: currentHub.startAddressLat,
                        lng: currentHub.startAddressLng,
                      }
                    : null
                }
              />
            </div>
            {selectedHubId && currentHub && (
              <div className="fleets-list">
                <div className="title">
                  <h2>{currentHub.name}</h2>
                  <Button type="link" onClick={handleCancelSelectedHub}>
                    {' '}
                    <CloseOutlined />{' '}
                  </Button>
                </div>
                <FleetsTable dataSource={currentHub.fleets || []} columns={fleetsColumns} pagination={false} />
              </div>
            )}
          </div>
        )}

        <Modal open={stateModal.open} onCancel={handleCancelModal} footer={[]} width={792} forceRender>
          <HubForm
            handleSubmit={handleSubmitModal}
            handleCancel={handleCancelModal}
            hub={stateModal.hub}
            action={stateModal.action}
            isFetching={isFetching}
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default HubsPage;
